import { Address } from 'shared/components/Address/AddressFields'
import { convertBooleanToYesNo } from 'shared/helpers/utils'
import { Address as AddressWithid } from 'shared/models/address'
import { DrivingAccident, DrivingViolation } from 'shared/models/employment'
import { DriverPayload, User } from 'shared/models/user'
import { mixed, object } from 'yup'

import { YesNo } from '../../../models'
import { SSN_ENCRYPTED } from './CDLInformationParser/CDLInformationParser.constants'

export const getDrivingHistorySchema = (accidents: DrivingAccident[], violations: DrivingViolation[]) =>
  object().shape({
    years_dry_van: mixed().test(function (val) {
      if (!val && val !== 0) {
        return this.createError({ message: 'This field is required.' })
      }
      return true
    }),
    years_flatbed: mixed().test(function (val) {
      if (!val && val !== 0) {
        return this.createError({ message: 'This field is required.' })
      }
      return true
    }),
    years_reefer: mixed().test(function (val) {
      if (!val && val !== 0) {
        return this.createError({ message: 'This field is required.' })
      }
      return true
    }),
    had_dui_criminal_charges: mixed().test(function (val) {
      if (!val) {
        return this.createError({
          message: 'This field is required.',
          path: 'had_dui_criminal_charges',
        })
      }

      if (val === YesNo.YES) {
        return this.createError({
          message: ' ',
          type: 'not_eligible',
          path: 'had_dui_criminal_charges',
        })
      }

      return true
    }),
    had_at_fault_incident: mixed().test(function (val) {
      if (!val) {
        return this.createError({
          message: 'This field is required.',
          path: 'had_at_fault_incident',
        })
      }
      if (val === YesNo.NO) return true

      if (!accidents?.length)
        return this.createError({ message: 'Please add accident.', path: 'had_at_fault_incident' })

      if (accidents?.some((a) => !a.isComplete))
        return this.createError({
          message: 'Please complete added accidents.',
          path: 'had_at_fault_incident',
        })

      return true
    }),
    had_major_violation: mixed().test(function (val) {
      if (!val) {
        return this.createError({
          message: 'This field is required.',
          path: 'had_major_violation',
        })
      }
      if (val === YesNo.NO) return true

      if (!violations?.length)
        return this.createError({ message: 'Please add violation.', path: 'had_major_violation' })

      if (violations?.some((a) => !a.isComplete))
        return this.createError({
          message: 'Please complete added violations.',
          path: 'had_major_violation',
        })

      return true
    }),
    had_privilege_denied: mixed().required('Please make a selection.'),
    had_privilege_denied_detail: mixed().test(function (val) {
      if (!val && this.parent.had_privilege_denied === YesNo.YES) {
        return this.createError({ message: 'This field is required.', path: 'had_privilege_denied_detail' })
      }
      return true
    }),
    had_privilege_suspended: mixed().required('Please make a selection.'),
    had_privilege_suspended_detail: mixed().test(function (val) {
      if (!val && this.parent.had_privilege_suspended === YesNo.YES) {
        return this.createError({
          message: 'This field is required.',
          path: 'had_privilege_suspended_detail',
        })
      }
      return true
    }),
  })

export type DrivingHistoryForm = {
  dob: string
  ssn: string
  address: Address
  years_dry_van?: number | string
  years_flatbed?: number | string
  years_reefer?: number | string
  had_dui_criminal_charges?: YesNo | null
  had_at_fault_incident?: YesNo | null
  had_major_violation?: YesNo | null
  had_privilege_denied?: YesNo | null
  had_privilege_suspended?: YesNo | null
  had_privilege_denied_detail?: string
  had_privilege_suspended_detail?: string
}

export const getInitialForm = (user: User): DrivingHistoryForm => {
  const driver = user.driver || {}

  return {
    dob: driver.dob || '',
    ssn: user.driver?.isDriverApplicationFormComplete ? SSN_ENCRYPTED : '',

    address: {
      line1: user.shippingAddress ? user.shippingAddress.line1 : '',
      line2: user.shippingAddress ? user.shippingAddress.line2 : '',
      city: user.shippingAddress ? user.shippingAddress.city : '',
      state: user.shippingAddress ? user.shippingAddress.state : '',
      zip: user.shippingAddress ? user.shippingAddress.zip : '',
    },

    years_dry_van: driver.yearsDryVan || '',
    years_flatbed: driver.yearsFlatbed || '',
    years_reefer: driver.yearsReefer || '',

    had_dui_criminal_charges: null,
    had_at_fault_incident: convertBooleanToYesNo(driver.hadAtFaultIncident),
    had_major_violation: convertBooleanToYesNo(driver.hadMajorViolation),
    had_privilege_denied: convertBooleanToYesNo(driver.hadPrivilegeDenied),
    had_privilege_suspended: convertBooleanToYesNo(driver.hadPrivilegeSuspended),
    had_privilege_denied_detail: user.hadPrivilegeDeniedDetail,
    had_privilege_suspended_detail: user.hadPrivilegeSuspendedDetail,
  }
}

export const getCreateDrivingHistoryBody = (applicationForm: Required<DrivingHistoryForm>): DriverPayload => {
  return {
    years_dry_van: Number(applicationForm.years_dry_van),
    years_flatbed: Number(applicationForm.years_flatbed),
    years_reefer: Number(applicationForm.years_reefer),
    had_at_fault_incident: applicationForm.had_at_fault_incident === YesNo.YES,
    had_major_violation: applicationForm.had_major_violation === YesNo.YES,
    had_privilege_denied: applicationForm.had_privilege_denied === YesNo.YES,
    had_privilege_suspended: applicationForm.had_privilege_suspended === YesNo.YES,
  }
}

export const getCreateAddressBody = (applicationForm: Required<DrivingHistoryForm>): AddressWithid => {
  return {
    id: '',
    line1: applicationForm.address.line1,
    line2: applicationForm.address.line2,
    city: applicationForm.address.city,
    state: applicationForm.address.state,
    country: '',
    zip: applicationForm.address.zip,
  }
}
